import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  UserCheck, Users, XCircle, Hourglass, QrCode, ChartLineUp,
  CheckCircle, WarningCircle
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon size={14} weight="duotone" style={{ color: accent }} />
        </div>
        <span className="text-[11px] text-[#555] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
      {sub && <p className="text-[11px] text-[#444] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminAttendance() {
  const [stats, setStats] = useState(null);
  const [pushStats, setPushStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, pRes] = await Promise.all([
          axios.get(`${API}/api/attendance/analytics/global`, { withCredentials: true }),
          axios.get(`${API}/api/push/stats`, { withCredentials: true }),
        ]);
        setStats(aRes.data);
        setPushStats(pRes.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return (
    <div className="space-y-4" data-testid="admin-attendance-loading">
      <div className="h-7 w-56 bg-white/5 rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-attendance-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Attendance & Push Analytics</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Event operations, check-ins, QR scans, and push delivery</p>
      </div>

      {/* Attendance Stats */}
      <h2 className="text-[13px] font-semibold text-[#888] uppercase tracking-wider mb-3">Attendance</h2>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total Records" value={stats.total_records} accent="#3B82F6" />
          <StatCard icon={UserCheck} label="Checked In" value={stats.checked_in} accent="#10B981" sub={`${stats.overall_checkin_rate}% rate`} />
          <StatCard icon={XCircle} label="No Shows" value={stats.no_show} accent="#EF4444" sub={`${stats.overall_no_show_rate}% rate`} />
          <StatCard icon={Hourglass} label="Waitlisted" value={stats.waitlisted} accent="#F59E0B" />
          <StatCard icon={QrCode} label="QR Scans" value={stats.qr_total_scans} accent="#9D4CDD" />
          <StatCard icon={CheckCircle} label="QR Success" value={stats.qr_successful} accent="#10B981" />
          <StatCard icon={WarningCircle} label="QR Failed" value={stats.qr_total_scans - stats.qr_successful} accent="#EF4444" />
          <StatCard icon={QrCode} label="Tokens Created" value={stats.tokens_created} accent="#3B82F6" />
        </div>
      )}

      {/* Push Stats */}
      <h2 className="text-[13px] font-semibold text-[#888] uppercase tracking-wider mb-3">Push Notifications</h2>
      {pushStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="Total Devices" value={pushStats.total_devices} accent="#9D4CDD" sub={`${pushStats.active_devices} active`} />
          <StatCard icon={ChartLineUp} label="Total Pushes" value={pushStats.total_deliveries} accent="#3B82F6" />
          <StatCard icon={CheckCircle} label="Delivered" value={pushStats.delivered} accent="#10B981" sub={`${pushStats.delivery_rate}% rate`} />
          <StatCard icon={XCircle} label="Failed" value={pushStats.failed} accent="#EF4444" />
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
        <h2 className="text-[14px] font-bold text-white mb-3">Quick Reference</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px]">
          <div className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
            <p className="text-white font-medium mb-1">Check-In Sources</p>
            <p className="text-[#555]">self_check_in, staff_check_in, qr_check_in, auto_promoted_from_waitlist</p>
          </div>
          <div className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
            <p className="text-white font-medium mb-1">Event Types</p>
            <p className="text-[#555]">session, shop_event, tournament, recurring_group_event, league_match_day</p>
          </div>
          <div className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
            <p className="text-white font-medium mb-1">Push Channels</p>
            <p className="text-[#555]">Web Push (VAPID) — supports desktop & mobile browsers</p>
          </div>
          <div className="px-3 py-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
            <p className="text-white font-medium mb-1">QR Security</p>
            <p className="text-[#555]">32-byte tokens, expiry, rotation, revocation, duplicate prevention</p>
          </div>
        </div>
      </div>
    </div>
  );
}
